import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Link, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import AuthPage from "../../components/auth/AuthPage";
import PasswordInput from "../../components/auth/PasswordInput";
import SubmitButton from "../../components/auth/SubmitButton";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../components/ui/form";
import { Input } from "../../components/ui/input";
import { API, ROLE_PASS } from "../../constant/api";
import { useAuth } from "../../context/auth-context";
import { loginSchema } from "../../utils/authSchemas";
import { getDeviceInfo } from "../../utils/device";
import { errorMessage } from "../../utils/errorMessage";
import sessionAxios from "../../utils/sessionAxios";

type LoginValues = { email: string; password: string };

const Login = () => {
  const { state } = useLocation();
  const { from = "/dashboard" } = state || {};
  const { authenticate, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const form = useForm<LoginValues>({
    resolver: yupResolver(loginSchema) as any,
    defaultValues: { email: "", password: "" },
    mode: "onTouched",
  });

  useEffect(() => {
    if (isAuthenticated) navigate("/dashboard");
  }, [isAuthenticated, navigate]);

  const onSubmit = (values: LoginValues) => {
    if (loading) return;
    setLoading(true);

    // Single sign-in call: the server decides whether 2FA is required and which
    // method to use.
    sessionAxios
      .post(API.login, {
        email: values.email,
        password: values.password,
        deviceInfo: getDeviceInfo(),
        rolePass: ROLE_PASS,
      })
      .then((r: any) => {
        setLoading(false);
        const data = r?.data?.data;

        // Branch on the 2FA marker, not on the presence of a token: under cookie auth
        // the session arrives as a Set-Cookie header, and `data.token` disappears
        // entirely once the backend stops returning it at the end of the migration.
        if (data?.status !== "2fa_required") {
          toast.success("You are logged in. Happy trading!!!");
          authenticate()
            .then(() => navigate(from))
            .catch(() =>
              toast.error("Signed in, but no session was established.")
            );
          return;
        }

        const next = { email: values.email, password: values.password, from };

        switch (data?.method) {
          case "email":
            return navigate("/2fa-auth", {
              state: { ...next, emailAuth: true, authApp: false },
            });
          case "auth-app":
            return navigate("/2fa-auth-app", {
              state: { ...next, emailAuth: false, authApp: true },
            });
          case "both":
            return navigate("/auth-select", {
              state: { ...next, emailAuth: true, authApp: true },
            });
          default:
            return toast.error("Unexpected 2FA response. Please try again.");
        }
      })
      .catch((e: any) => {
        setLoading(false);
        toast.error(
          errorMessage(
            e?.response?.data?.message || "An error occurred during login"
          )
        );
      });
  };

  return (
    <AuthPage
      title="👋🏽 Welcome Back!"
      description="Sign in and let's begin seamless trading."
      footer={
        <span className="text-foreground">
          Don't have an account?{" "}
          <Link to="/signup" className="font-semibold text-primary underline">
            Sign Up
          </Link>
        </span>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="e.g. email@domain.com"
                    {...field}
                    onChange={(e) => field.onChange(e.target.value.toLowerCase())}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div>
            <Link
              to="/forgot-password"
              className="text-sm font-bold text-primary"
            >
              Forgot password?
            </Link>
          </div>

          <SubmitButton loading={loading}>Sign In</SubmitButton>
        </form>
      </Form>
    </AuthPage>
  );
};

export default Login;
